/**
 * Vuex Store for webhooks settings
 * @author      Jeff Adams <jeff@tinyfly.com>
 * @copyright   Traxo, Inc. 2018
 */

import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import { WebhooksApi } from '@/services/api-webhooks.js';

function defaultStateFactory() {
  return {
    webhooks: [],
    pagination: {},
    webhook: {},
  };
}

export const state = defaultStateFactory();

export const mutations = {
  setWebhooks(state, { meta = {}, data = [] }) {
    state.pagination = meta;
    state.webhooks = data;
  },

  setWebhook(state, webhook = {}) {
    state.webhook = webhook;
  },

  addWebhook(state, webhook = {}) {
    state.webhooks.push(webhook);
  },

  resetState(state, defaultState) {
    state = defaultState;
  },
};

export const getters = {
  webhooksSortedByStatus: (state) => sortBy(state.webhooks, ['status']),

  webhooksGroupedByStatus: (state, getters) => groupBy(getters.webhooksSortedByStatus, 'status'),

  webhookById: (state) => (id) => state.webhooks.find((webhook) => webhook.id === id),
};

export const actions = {
  fetch({ commit, state }, { query }) {
    return WebhooksApi.getWebhooks(query)
      .then((webhooks) => {
        commit('setWebhooks', webhooks);
        return state.webhooks;
      })
      .catch((err) => {
        commit('setWebhooks', {});
        throw err;
      });
  },

  create({ commit }, { data }) {
    return WebhooksApi.createWebhook(data).then((webhook) => {
      commit('addWebhook', webhook);
      return webhook;
    });
  },

  read({ commit, getters, state }, { id }) {
    // check for an already fetched webhook
    const webhook = getters.webhookById(id);
    if (webhook) {
      commit('setWebhook', webhook);
      return Promise.resolve(webhook);
    }

    // otherwise fetch again.
    return WebhooksApi.getWebhook(id)
      .then((webhook) => {
        commit('setWebhook', webhook);
        return state.webhook;
      })
      .catch((err) => {
        commit('setWebhook', {});
        throw err;
      });
  },

  update({ commit, state }, { id, data }) {
    return WebhooksApi.updateWebhook(id, data).then((webhook) => {
      commit('setWebhook', webhook);
      return state.webhook;
    });
  },

  delete({ commit }, { id }) {
    return WebhooksApi.deleteWebhook(id).then((response) => {
      commit('setWebhook', response);
      return response;
    });
  },

  /**
   * Sets the state back to the default values
   * @returns {Promise}
   */
  reset({ commit }) {
    commit('resetState', defaultStateFactory());
  },
};
